import { Skeleton, Space } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import useAnalytics from 'config/analytics';
import { css } from 'config/emotionCss';
import colors from 'config/theme/colors';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { useRights } from 'hooks';
import useLocalUserPreferences, { USER_PREFERENCES } from 'hooks/useLocalUserPreferences';
import { getDefaultLanguage } from 'modules/checklists/selectors/details';
import { getLocale } from 'modules/i18n/selectors/data';
import { fetchReleases } from 'modules/releases/actions/core';
import { closeReleaseModal, updateReleaseModal } from 'modules/releases/actions/release-modal';
import { RELEASE_MODAL_FETCH_PARAMS } from 'modules/releases/sagas/release-modal';
import { getDisplayableReleases, getReleaseToDisplay, isReleaseModalInPreviewMode } from 'modules/releases/selectors/release-modal';
import { getCurrentRoute } from 'services/RouterUtils';
import Button from 'ui/base/Button';
import Modal from 'ui/base/Modal';
import Select from 'ui/base/Select';
import { getLabelTranslation } from 'utils/global';

const imageContainerStyle = css`
  max-width: 100%;
  aspect-ratio: ${940 / 400};
  background-color: rgba(0, 0, 0, 0.06);
`;

const detailsStyle = css`
  min-height: 250px;
  padding: 24px 120px;
  font-size: 18px;
`;

const indicatorStyle = css`
  width: 10px;
  height: 10px;
  background-color: ${colors['@background-overlay']};

  &.current {
    background-color: ${colors['@primary-color']};
  }
`;

const CTA_EVENT_BASE_PAYLOAD = {
  category: 'Release Note',
  action: 'Release note link'
};

const ReleaseNoteModal = () => {
  const dispatch = useDispatch();
  const { onClick } = useAnalytics();

  const { hasAccess } = useRights();
  const { setPreference } = useLocalUserPreferences();

  const appLocale = useSelector(getLocale);
  const defaultLocale = useSelector(getDefaultLanguage);
  const releases = useSelector(getDisplayableReleases);
  const releaseToDisplay = useSelector(getReleaseToDisplay);
  const isPreviewMode = useSelector(isReleaseModalInPreviewMode);

  const [noteIndex, setNoteIndex] = React.useState(0);
  const maxIndex = releaseToDisplay?.notes ? releaseToDisplay.notes.length - 1 : 0;

  React.useEffect(() => {
    setNoteIndex(0);
  }, [releaseToDisplay.id]);

  const handleClose = React.useCallback(() => {
    if (!isPreviewMode && releases.length > 0) {
      setPreference(USER_PREFERENCES.LAST_RELEASE_NOTE, {
        id: releases[0].id,
        etag: releases[0].etag,
        releaseVersion: releases[0].releaseVersion,
      });
    }
    dispatch(closeReleaseModal());
  }, [dispatch, isPreviewMode, releases, setPreference]);
  const handlePreviousButton = React.useCallback(() => setNoteIndex(prev => Math.max(prev - 1, 0)), []);
  const handleNextButton = React.useCallback(() => setNoteIndex(prev => Math.min(prev + 1, maxIndex)), [maxIndex]);
  const handleChangeRelease = React.useCallback((releaseId) => dispatch(updateReleaseModal(releaseId)), [dispatch]);

  const [pageNumber, setPageNumber] = React.useState(1);
  const handleLoadNextPage = React.useCallback(() => {
    const page = pageNumber + 1;
    dispatch(fetchReleases({ ...RELEASE_MODAL_FETCH_PARAMS, page }));
    setPageNumber(page);
  }, [dispatch, pageNumber]);

  /** @type {ReleaseNoteContent} */
  const note = React.useMemo(
    () => {
      const translatedNote = getLabelTranslation(releaseToDisplay?.notes?.[noteIndex]?.content, appLocale, defaultLocale);
      const fallback = releaseToDisplay?.notes?.[noteIndex]?.content?.[DEFAULT_LANGUAGE];

      return { ...fallback, ...translatedNote };
    },
    [releaseToDisplay, noteIndex, appLocale, defaultLocale]
  );

  const analyticsPayload = React.useMemo(() => ({
    ...CTA_EVENT_BASE_PAYLOAD,
    label: `CTA-${releaseToDisplay.releaseVersion}-${note?.link?.displayText}`
  }), [note, releaseToDisplay.releaseVersion]);

  const isLoading = Boolean(releaseToDisplay?.media); // It's loading when you don't have the detailed release yet (current projection = page)

  const options = React.useMemo(() => (isPreviewMode ? [releaseToDisplay] : releases), [isPreviewMode, releases, releaseToDisplay]);

  const shouldDisplayLink = React.useMemo(() => {
    if (!note?.link?.targetUrl) {
      return false;
    }
    const { origin } = window.location;
    const isFleetbackUrl = note.link.targetUrl.startsWith(origin);
    if (isFleetbackUrl) {
      const { pathname } = new URL(note.link.targetUrl);
      const route = getCurrentRoute(pathname);
      return hasAccess(route);
    }
    return true;
  }, [hasAccess, note]);

  return (
    <Modal
      width={940}
      classNames={{ container: 'p-0 overflow-hidden rounded-xl', footer: 'p-4 d-flex justify-content-between' }}
      footer={[
        <div>
          {noteIndex !== maxIndex && (
            <Button onClick={handleClose} data-cy="cancel-release-note">
              <FormattedMessage id="skip" defaultMessage="Skip" />
            </Button>
          )}
        </div>,
        <Space size={12}>
          {noteIndex > 0 && (
            <Button onClick={handlePreviousButton} data-cy="previous-release-note">
              <FormattedMessage id="previous" defaultMessage="Previous" />
            </Button>
          )}
          {noteIndex < maxIndex && (
            <Button type="primary" onClick={handleNextButton} data-cy="next-release-note">
              <FormattedMessage id="next" defaultMessage="Next" />
            </Button>
          )}
          {noteIndex === maxIndex && (
            <Button type="primary" onClick={handleClose} data-cy="close-release-note">
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
          )}
        </Space>,
      ]}
      mask={{ closable: false }}
      data-cy="releases-modal"
      open
    >
      <div className={classNames('d-flex w-100 align-items-center justify-content-center', imageContainerStyle)}>
        {isLoading ? (
          <Skeleton.Image active className="w-100 h-100" />
        ) : (
          <img src={note?.media?.path} alt={note?.title} className="w-auto h-100" />
        )}
      </div>
      <div className={classNames(detailsStyle, 'd-flex flex-column gap-3 text-lg')}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2-5">
            {maxIndex > 0 &&
              Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <span key={index} className={classNames('rounded-circle', indicatorStyle, { current: index === noteIndex })} />
              ))}
          </div>
          <div>
            <Select
              variant="borderless"
              options={options}
              disabled={isPreviewMode}
              value={releaseToDisplay?.id}
              onSelect={handleChangeRelease}
              data-cy="release-version-select"
              loadMore={handleLoadNextPage}
              fieldNames={{ label: 'releaseVersion', value: 'id' }}
              popupMatchSelectWidth={false}
            />
          </div>
        </div>
        {isLoading && <Skeleton active />}
        {note?.name && (
          <span className="font-weight-bold" data-cy="release-name">
            {note.name}
          </span>
        )}
        {note?.description && (
          <p className="mb-0 text-secondary text-sm" data-cy="release-description">
            {note.description}
          </p>
        )}
        {shouldDisplayLink && (
          <a href={note.link.targetUrl} onClick={onClick(analyticsPayload)} target="_blank" rel="noreferrer" className="text-sm text-underline">
            {note.link.displayText}
          </a>
        )}
      </div>
    </Modal>
  );
};

export default ReleaseNoteModal;
